import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import type { UserDto } from '@bstm/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { toast } from 'sonner';

export default function SetName() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const [name, setName] = useState('');

  const save = useMutation({
    mutationFn: (value: string) =>
      api<{ user: UserDto }>('/me', { method: 'PATCH', json: { name: value } }),
    onSuccess: ({ user: updated }) => {
      qc.invalidateQueries({ queryKey: ['me'] });
      toast.success('Welcome aboard');
      if (updated.status !== 'ACTIVE') {
        navigate(`/pending${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`);
      } else {
        navigate(next);
      }
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 1) {
      toast.error('Enter your name');
      return;
    }
    if (trimmed.length > 80) {
      toast.error('Name too long');
      return;
    }
    save.mutate(trimmed);
  }

  return (
    <div className="min-h-full flex items-center justify-center p-6">
      <div className="card p-8 w-full max-w-sm">
        <h1 className="text-xl font-semibold mb-1">One last step</h1>
        <p className="text-sm text-zinc-400 mb-6">
          Tell us your name so the team knows who's submitting tickets.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label" htmlFor="name">Display name</label>
            <input
              id="name"
              type="text"
              autoFocus
              autoComplete="name"
              maxLength={80}
              className="w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sarah Chen"
            />
          </div>
          <button
            type="submit"
            className="btn-primary w-full justify-center"
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Continue'}
          </button>
        </form>
        <div className="mt-6 pt-4 border-t border-zinc-800 text-center">
          <p className="text-xs text-zinc-500 mb-2 font-mono">Signed in as {user?.phone}</p>
          <button onClick={logout} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
